import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { mergeSubscriptionParameters } from '@/api/auth'
import { usersApi } from '@/api/users'
import type { User, UserFilters, CreateUserData } from '@/types'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  TablePagination,
  Alert,
  Skeleton,
  Menu,
  Chip,
  Card,
  CardContent,
  Link as MuiLink,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Breadcrumbs,
} from '@mui/material'
import Link from '@mui/material/Link'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { FiltersButton } from '@/components/common/FiltersButton'
import { useTheme } from '@mui/material/styles'
import { useMediaQuery } from '@mui/material'
import {
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Login as LoginIcon,
  MoreVert as MoreVertIcon,
  Add as AddIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material'
import { Link as RouterLink } from 'react-router-dom'
import { ROUTES } from '@/utils/constants'
import { parseSortValue } from '@/utils/sort'
import { bytesToMb } from '@/utils/storage'
import { getErrorMessage } from '@/utils/error'
import { useAuthStore } from '@/stores/authStore'
import { STORAGE_KEYS } from '@/utils/constants'
import '@/styles/dashboard.css'

type SortBy = UserFilters['sort_by']

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  active: 'success',
  inactive: 'default',
  suspended: 'warning',
}

export function AdminUsersPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const token = useAuthStore((s) => s.token)
  const setAuthImpersonated = useAuthStore((s) => s.setAuthImpersonated)
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [storageTotalBytes, setStorageTotalBytes] = useState<number | null>(null)
  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    per_page: 15,
    sort_by: 'id',
    sort_order: 'desc',
  })
  const [searchInput, setSearchInput] = useState('')
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [menuUser, setMenuUser] = useState<User | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState<CreateUserData & { role: 'student' | 'teacher' | 'admin' }>({
    surname: '',
    first_name: '',
    middle_name: '',
    email: '',
    password: '',
    role: 'student',
  })
  const [createSubmitting, setCreateSubmitting] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)

  /**
   * Счётчик для кнопки «Фильтры»: роль, статус и подписка — три селекта
   * свёрнутой панели. Поиск и сортировка стоят в тулбаре и всегда видны,
   * поэтому в счёт не идут: иначе бейдж горел бы при пустой панели.
   */
  const appliedFiltersCount = useMemo(() => {
    let count = 0
    if (filters.role !== undefined) count += 1
    if (filters.status !== undefined) count += 1
    if (filters.subscription !== undefined) count += 1

    return count
  }, [filters.role, filters.status, filters.subscription])

  const loadUsers = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await usersApi.getAll(filters)
      setUsers(res.data)
      setTotal(res.total)
      setStorageTotalBytes(res.storage_total_bytes ?? null)
    } catch (err: unknown) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.per_page, filters.role, filters.status, filters.subscription, filters.search, filters.sort_by, filters.sort_order])

  // Поиск без кнопки: обновление фильтра с задержкой при вводе
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((prev) => {
        const nextSearch = searchInput.trim() || undefined
        if (prev.search === nextSearch) return prev
        return { ...prev, search: nextSearch, page: 1 }
      })
    }, 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  const handlePageChange = (_: unknown, newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage + 1 }))
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const perPage = parseInt(e.target.value, 10)
    setFilters((prev) => ({ ...prev, per_page: perPage, page: 1 }))
  }

  const openMenu = (e: React.MouseEvent<HTMLElement>, u: User) => {
    e.stopPropagation()
    setMenuAnchor(e.currentTarget)
    setMenuUser(u)
  }

  const closeMenu = () => {
    setMenuAnchor(null)
    setMenuUser(null)
  }

  const handleView = () => {
    if (menuUser) navigate(ROUTES.ADMIN_USER_VIEW(menuUser.id))
    closeMenu()
  }

  const handleImpersonate = async () => {
    if (!menuUser || !token) return
    closeMenu()
    localStorage.setItem(STORAGE_KEYS.ADMIN_TOKEN, token)
    try {
      const res = await usersApi.impersonate(menuUser.id)
      const user = await mergeSubscriptionParameters(res.user)
      setAuthImpersonated(user, res.token)
      navigate(ROUTES.DASHBOARD, { replace: true })
    } catch (err: unknown) {
      setError(getErrorMessage(err) || t('common.error'))
    }
  }

  const getFullName = (u: User) => {
    const parts = [u.surname, u.first_name, u.middle_name].filter(Boolean)
    return parts.length ? parts.join(' ') : u.email
  }

  const hasTeacherRole = (u: User) => u.roles?.some((r) => r.slug === 'teacher') ?? false

  const subscriptionLabel = (u: User) => {
    if (!hasTeacherRole(u)) return '—'
    const sub = u.active_subscription
    if (!sub) return t('admin.users.noSubscription')
    return sub.plan_name || t('admin.users.noSubscription')
  }

  const rolesLabel = (u: User) => {
    if (!u.roles?.length) return '—'
    return u.roles.map((r) => (r.slug === 'student' ? t('admin.users.roleStudent') : r.slug === 'teacher' ? t('admin.users.roleTeacher') : r.slug === 'admin' ? t('admin.users.roleAdmin') : r.slug)).join(', ')
  }

  const statusLabel = (s: string) => {
    if (s === 'active') return t('admin.users.statusActive')
    if (s === 'inactive') return t('admin.users.statusInactive')
    if (s === 'suspended') return t('admin.users.statusSuspended')
    return s
  }

  const handleCreateUser = async () => {
    if (!createForm.surname?.trim() || !createForm.first_name?.trim() || !createForm.email?.trim() || !createForm.password) {
      setCreateError(t('users.createValidationRequired'))
      return
    }
    setCreateSubmitting(true)
    setCreateError(null)
    try {
      await usersApi.create({
        surname: createForm.surname.trim(),
        first_name: createForm.first_name.trim(),
        middle_name: createForm.middle_name?.trim() || undefined,
        email: createForm.email.trim(),
        password: createForm.password,
        role: createForm.role,
      })
      setCreateOpen(false)
      setCreateForm({ surname: '', first_name: '', middle_name: '', email: '', password: '', role: 'student' })
      loadUsers()
    } catch (err: unknown) {
      setCreateError(getErrorMessage(err) || t('common.error'))
    } finally {
      setCreateSubmitting(false)
    }
  }

  const closeCreateModal = () => {
    setCreateOpen(false)
    setCreateForm({ surname: '', first_name: '', middle_name: '', email: '', password: '', role: 'student' })
    setCreateError(null)
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('dashboard.administration')}</Typography>
          <Typography color="text.primary">{t('admin.users.title')}</Typography>
        </Breadcrumbs>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('admin.users.title')}
          </Typography>
          <PageHelpPopover pageKey="admin-users" />
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder={t('common.search')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: isMobile ? 120 : 200, flex: isMobile ? 1 : undefined }}
          />
          <FormControl size="small" sx={{ minWidth: isMobile ? 140 : 180 }}>
            <InputLabel>{t('admin.users.sortBy')}</InputLabel>
            <Select
              value={`${filters.sort_by || 'id'}_${filters.sort_order || 'desc'}`}
              label={t('admin.users.sortBy')}
              onChange={(e) => {
                const [sort_by, sort_order] = parseSortValue<NonNullable<SortBy>>(e.target.value as string)
                setFilters((prev) => ({ ...prev, sort_by, sort_order, page: 1 }))
              }}
            >
              <MenuItem value="id_desc">{t('admin.users.sortIdDesc')}</MenuItem>
              <MenuItem value="id_asc">{t('admin.users.sortIdAsc')}</MenuItem>
              <MenuItem value="surname_asc">{t('admin.users.sortSurnameAsc')}</MenuItem>
              <MenuItem value="surname_desc">{t('admin.users.sortSurnameDesc')}</MenuItem>
              <MenuItem value="email_asc">{t('admin.users.sortEmailAsc')}</MenuItem>
              <MenuItem value="email_desc">{t('admin.users.sortEmailDesc')}</MenuItem>
              <MenuItem value="created_at_desc">{t('admin.users.sortCreatedDesc')}</MenuItem>
              <MenuItem value="created_at_asc">{t('admin.users.sortCreatedAsc')}</MenuItem>
              <MenuItem value="status_asc">{t('admin.users.sortStatusAsc')}</MenuItem>
              <MenuItem value="status_desc">{t('admin.users.sortStatusDesc')}</MenuItem>
            </Select>
          </FormControl>
          <FiltersButton
            open={filtersOpen}
            count={appliedFiltersCount}
            onToggle={() => setFiltersOpen((o) => !o)}
            label={t('admin.users.filtersButton')}
          />
          <Box sx={{ ml: isMobile ? 0 : 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{ height: 40 }}
            >
              {t('admin.users.createUser')}
            </Button>
          </Box>
        </Box>

        <Collapse in={filtersOpen}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
              <InputLabel>{t('admin.users.filterRole')}</InputLabel>
              <Select
                value={filters.role || 'all'}
                label={t('admin.users.filterRole')}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    role: e.target.value === 'all' ? undefined : (e.target.value as UserFilters['role']),
                    page: 1,
                  }))
                }
              >
                <MenuItem value="all">{t('admin.users.all')}</MenuItem>
                <MenuItem value="student">{t('admin.users.roleStudent')}</MenuItem>
                <MenuItem value="teacher">{t('admin.users.roleTeacher')}</MenuItem>
                <MenuItem value="admin">{t('admin.users.roleAdmin')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 120 } }}>
              <InputLabel>{t('admin.users.filterStatus')}</InputLabel>
              <Select
                value={filters.status || 'all'}
                label={t('admin.users.filterStatus')}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    status: e.target.value === 'all' ? undefined : (e.target.value as UserFilters['status']),
                    page: 1,
                  }))
                }
              >
                <MenuItem value="all">{t('admin.users.all')}</MenuItem>
                <MenuItem value="active">{t('admin.users.statusActive')}</MenuItem>
                <MenuItem value="inactive">{t('admin.users.statusInactive')}</MenuItem>
                <MenuItem value="suspended">{t('admin.users.statusSuspended')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 140 } }}>
              <InputLabel>{t('admin.users.filterSubscription')}</InputLabel>
              <Select
                value={filters.subscription || 'all'}
                label={t('admin.users.filterSubscription')}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    subscription: e.target.value === 'all' ? undefined : (e.target.value as UserFilters['subscription']),
                    page: 1,
                  }))
                }
              >
                <MenuItem value="all">{t('admin.users.all')}</MenuItem>
                <MenuItem value="with">{t('admin.users.subscriptionWith')}</MenuItem>
                <MenuItem value="without">{t('admin.users.subscriptionWithout')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Collapse>

        {loading && storageTotalBytes !== null ? (
          <Typography variant="body2" sx={{ mb: 1 }}>
            <Skeleton width={220} />
          </Typography>
        ) : storageTotalBytes !== null ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('admin.users.storageTotal', 'Всего занято аудио: {{mb}} МБ', {
              mb: bytesToMb(storageTotalBytes),
            })}
          </Typography>
        ) : null}

        {loading ? (
          isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Card key={i}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="40%" height={20} sx={{ mb: 0.5 }} />
                        <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 1, mt: 0.5 }} />
                        <Skeleton variant="text" width="50%" height={20} sx={{ mt: 0.5 }} />
                      </Box>
                      <Skeleton variant="circular" width={32} height={32} />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>{t('admin.users.fullName')}</TableCell>
                    <TableCell>{t('common.email')}</TableCell>
                    <TableCell>{t('admin.users.roles')}</TableCell>
                    <TableCell>{t('admin.users.status')}</TableCell>
                    <TableCell>{t('admin.users.subscription')}</TableCell>
                    <TableCell>{t('admin.users.storage', 'Диск')}</TableCell>
                    <TableCell align="right">{t('admin.users.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <TableRow key={i}>
                      <TableCell><Skeleton variant="text" width={24} /></TableCell>
                      <TableCell><Skeleton variant="text" width={120} /></TableCell>
                      <TableCell><Skeleton variant="text" width={160} /></TableCell>
                      <TableCell><Skeleton variant="text" width={80} /></TableCell>
                      <TableCell><Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                      <TableCell><Skeleton variant="text" width={90} /></TableCell>
                      <TableCell><Skeleton variant="text" width={60} /></TableCell>
                      <TableCell align="right"><Skeleton variant="circular" width={32} height={32} sx={{ ml: 'auto' }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : isMobile ? (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {users.map((u) => (
                <Card key={u.id}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                      <Box>
                        <MuiLink component={RouterLink} to={ROUTES.ADMIN_USER_VIEW(u.id)} underline="hover" sx={{ fontWeight: 600 }}>
                          {getFullName(u)}
                        </MuiLink>
                        <Typography variant="body2" color="text.secondary">
                          {u.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {rolesLabel(u)}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                          {u.status && (
                            <Chip label={statusLabel(u.status)} size="small" color={STATUS_COLORS[u.status] || 'default'} />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {subscriptionLabel(u)}
                        </Typography>
                        {u.storage_usage && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {t('admin.users.storage', 'Диск')}:{' '}
                            {t('admin.users.storageMb', '{{mb}} МБ', {
                              mb: bytesToMb(u.storage_usage.used_bytes),
                            })}
                          </Typography>
                        )}
                      </Box>
                      <IconButton size="small" onClick={(e) => openMenu(e, u)}>
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, width: '100%', minWidth: 0 }}>
              <TablePagination
                component="div"
                count={total}
                page={Math.max(0, (filters.page || 1) - 1)}
                onPageChange={handlePageChange}
                rowsPerPage={filters.per_page || 15}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 15, 25, 50]}
                labelRowsPerPage={t('cards.rows_per_page')}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} ${t('cards.of')} ${count !== -1 ? count : `${t('cards.more_than')} ${to}`}`
                }
                sx={{
                  width: '100%',
                  overflow: 'hidden',
                  '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', rowGap: 1 },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { m: 0 },
                }}
              />
            </Box>
          </>
        ) : (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>{t('admin.users.fullName')}</TableCell>
                    <TableCell>{t('common.email')}</TableCell>
                    <TableCell>{t('admin.users.roles')}</TableCell>
                    <TableCell>{t('admin.users.status')}</TableCell>
                    <TableCell>{t('admin.users.subscription')}</TableCell>
                    <TableCell>{t('admin.users.storage', 'Диск')}</TableCell>
                    <TableCell align="right">{t('admin.users.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>{u.id}</TableCell>
                      <TableCell>
                        <MuiLink component={RouterLink} to={ROUTES.ADMIN_USER_VIEW(u.id)} underline="hover" sx={{ fontWeight: 600 }}>
                          {getFullName(u)}
                        </MuiLink>
                      </TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>{rolesLabel(u)}</TableCell>
                      <TableCell>
                        {u.status ? (
                          <Chip label={statusLabel(u.status)} size="small" color={STATUS_COLORS[u.status] || 'default'} />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{subscriptionLabel(u)}</TableCell>
                      <TableCell>
                        {u.storage_usage
                          ? t('admin.users.storageMb', '{{mb}} МБ', { mb: bytesToMb(u.storage_usage.used_bytes) })
                          : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={(e) => openMenu(e, u)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, flexWrap: 'wrap', width: '100%', minWidth: 0 }}>
              <TablePagination
                component="div"
                count={total}
                page={Math.max(0, (filters.page || 1) - 1)}
                onPageChange={handlePageChange}
                rowsPerPage={filters.per_page || 15}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 15, 25, 50]}
                labelRowsPerPage={t('cards.rows_per_page')}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} ${t('cards.of')} ${count !== -1 ? count : `${t('cards.more_than')} ${to}`}`
                }
                sx={{
                  overflow: 'hidden',
                  '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', rowGap: 1 },
                }}
              />
            </Box>
          </>
        )}

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={handleView}>
            <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
            {t('common.view')}
          </MenuItem>
          <MenuItem onClick={handleImpersonate}>
            <LoginIcon sx={{ mr: 1 }} fontSize="small" />
            {t('admin.users.impersonate')}
          </MenuItem>
        </Menu>

        <Dialog open={createOpen} onClose={closeCreateModal} maxWidth="sm" fullWidth>
          <DialogTitle>{t('admin.users.createUser')}</DialogTitle>
          <DialogContent>
            {createError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateError(null)}>
                {createError}
              </Alert>
            )}
            <TextField
              fullWidth
              size="small"
              label={t('auth.surname')}
              required
              value={createForm.surname}
              onChange={(e) => setCreateForm((p) => ({ ...p, surname: e.target.value }))}
              sx={{ mt: 1 }}
            />
            <TextField
              fullWidth
              size="small"
              label={t('auth.firstName')}
              required
              value={createForm.first_name}
              onChange={(e) => setCreateForm((p) => ({ ...p, first_name: e.target.value }))}
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              label={t('auth.middleName')}
              value={createForm.middle_name}
              onChange={(e) => setCreateForm((p) => ({ ...p, middle_name: e.target.value }))}
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              label={t('auth.email')}
              type="email"
              required
              value={createForm.email}
              onChange={(e) => setCreateForm((p) => ({ ...p, email: e.target.value }))}
              sx={{ mt: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              label={t('auth.password')}
              type="password"
              required
              value={createForm.password}
              onChange={(e) => setCreateForm((p) => ({ ...p, password: e.target.value }))}
              sx={{ mt: 2 }}
              helperText={t('auth.validation.passwordMin')}
            />
            <FormControl fullWidth size="small" sx={{ mt: 2 }}>
              <InputLabel>{t('admin.users.filterRole')}</InputLabel>
              <Select
                value={createForm.role}
                label={t('admin.users.filterRole')}
                onChange={(e) => setCreateForm((p) => ({ ...p, role: e.target.value as 'student' | 'teacher' | 'admin' }))}
              >
                <MenuItem value="student">{t('admin.users.roleStudent')}</MenuItem>
                <MenuItem value="teacher">{t('admin.users.roleTeacher')}</MenuItem>
                <MenuItem value="admin">{t('admin.users.roleAdmin')}</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={closeCreateModal}>{t('common.cancel')}</Button>
            <Button variant="contained" onClick={handleCreateUser} disabled={createSubmitting}>
              {createSubmitting ? t('common.loading') : t('common.create')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
